import React, { useState, useEffect } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Divider
} from "@material-ui/core";
import { createFeeCalculation, getCalculatedFee,downloadFeeCalculationReport  } from "../../service";
import SearchCaseForm from "../SearchCase";
import { getCaseReferenceNumber, isAllowedAmountInput } from "../../utils";

const PRAYER_DESCRIPTION_LIMIT = 200;
const MAX_PRAYERS = 100;
const PROBONO_ELIGIBLE_LIMIT = 500000;
const PRAYER_FEE_PER_PRAYER = 100000;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
    borderRadius: 8,
    border: "1px solid #E5E5E5",
    boxShadow: "none",
    maxWidth: 900,
    margin: "0 auto",
    textAlign: "left"
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "1rem",
    marginBottom: theme.spacing(1.5),
    color: TEXT.primary
  },
  label: {
    fontWeight: 500,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1),
    color: TEXT.secondary,
    display: "block"
  },
  caseCard: {
    padding: theme.spacing(2),
    border: "1px solid #E5E5E5",
    borderRadius: 8,
    backgroundColor: "#FAFAFA",
    marginBottom: theme.spacing(3)
  },
  detailLabel: {
    fontSize: "0.75rem",
    color: TEXT.muted,
    textTransform: "uppercase",
    letterSpacing: "0.02em"
  },
  detailValue: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: TEXT.strong,
    wordBreak: "break-word"
  },
  calculateBtn: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.25, 4),
    fontSize: "1rem",
    textTransform: "none",
    borderRadius: 6,
    fontWeight: 600
  },
  resultCard: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
    border: "1px solid #E5E5E5",
    boxShadow: "none",
    borderRadius: 8,
    backgroundColor: "#FAFAFA"
  },
  resultTitle: {
    fontWeight: 700,
    fontSize: "1.25rem",
    marginBottom: theme.spacing(2),
    color: TEXT.strong
  },
  feeRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    borderBottom: "1px solid #E5E5E5",
    "&:last-child": {
      borderBottom: "none"
    }
  },
  feeLabel: {
    fontSize: "0.95rem",
    fontWeight: 500,
    color: TEXT.secondary
  },
  feeValue: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: TEXT.strong,
    minWidth: 0,
    textAlign: "right",
    overflowWrap: "anywhere"
  },
  plusSign: {
    fontWeight: 700,
    color: TEXT.info,
    marginRight: theme.spacing(0.75)
  },
  feeSubLabel: {
    fontSize: "0.75rem",
    color: TEXT.muted,
    marginTop: 2
  },
  editableFeeInput: {
    width: 180,
    backgroundColor: "#ffffff"
  },
  noteBox: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(1.5),
    borderLeft: "3px solid #2F80ED"
  },
  noteText: {
    fontSize: "0.75rem",
    color: TEXT.muted,
    lineHeight: 1.5,
    fontStyle: "italic",
    textAlign: "justify"
  },
  totalFeeCard: {
    backgroundColor: "#EBF2FF",
    border: "1px solid #B4D3FF",
    padding: theme.spacing(2),
    borderRadius: 6,
    marginBottom: theme.spacing(3),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2)
  },
  totalFeeLabel: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: TEXT.info,
    flexShrink: 0
  },
  totalFeeValue: {
    fontWeight: 700,
    fontSize: "1.35rem",
    color: TEXT.info,
    minWidth: 0,
    textAlign: "right",
    overflowWrap: "anywhere"
  },
  errorText: {
    color: TEXT.error,
    fontSize: "0.875rem",
    marginTop: theme.spacing(1)
  },
  successBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 6,
    backgroundColor: "#E4F5EA",
    border: "1px solid #A9DFBF",
    color: TEXT.success,
    fontSize: "0.875rem",
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
  }
}));

export default function FeeCalculator({
  defaultAmount = "",
  currency = "INR",
  onCalculationComplete
}) {
  const classes = useStyles();

  const [selectedCase, setSelectedCase] = useState(null);
  const [sumOfDispute, setSumOfDispute] = useState(defaultAmount);
  const [numberOfPrayers, setNumberOfPrayers] = useState("");
  const [prayers, setPrayers] = useState([]);
  const [proBono, setProBono] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Which input the current error belongs to, so only that field turns red.
  const [errorField, setErrorField] = useState("");

  // Saving the calculation to the Financial > Fee Calculations register.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [savedEntry, setSavedEntry] = useState(null);
  const [result, setResult] = useState(null);
  // Administrative Expenses is seeded from the calculation but stays editable,
  const [adminExpense, setAdminExpense] = useState("");

  // Downloading/printing the saved report
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState("");

  // Monitor sum of dispute to toggle pro-bono availability
  const numericAmount = parseFloat(sumOfDispute);
  const isProBonoAvailable =
    !isNaN(numericAmount) && numericAmount <= PROBONO_ELIGIBLE_LIMIT;

  const prayerCount = parseInt(numberOfPrayers, 10);
  const hasValidPrayerCount = !isNaN(prayerCount) && prayerCount > 0;

  useEffect(() => {
    // If the amount goes above 5,00,000, uncheck pro-bono
    if (!isProBonoAvailable) {
      setProBono(false);
    }
  }, [sumOfDispute, isProBonoAvailable]);

  useEffect(() => {
    if (!hasValidPrayerCount) {
      setPrayers([]);
      return;
    }
    const count = Math.min(prayerCount, MAX_PRAYERS);
    setPrayers((prev) => Array.from({ length: count }, (_, i) => prev[i] || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfPrayers]);

  const handleCaseSearchSuccess = (data) => {
    const disputeData = data?.dispute_data || data;
    if (disputeData && disputeData.id) {
      setSelectedCase(disputeData);
      // The case already carries the claim amount used by the Fee Schedule,
      // so pre-fill it and let the officer confirm or override it.
      setSumOfDispute(
        disputeData.claim_amount ? String(disputeData.claim_amount) : defaultAmount
      );
      setError("");
      setErrorField("");
      setResult(null);
      setAdminExpense("");
      setSubmitError("");
      setSavedEntry(null);
      setPrintError("");
    }
  };

  const handleChangeCase = () => {
    setSelectedCase(null);
    setSumOfDispute(defaultAmount);
    setNumberOfPrayers("");
    setPrayers([]);
    setProBono(false);
    setResult(null);
    setAdminExpense("");
    setError("");
    setErrorField("");
    setSubmitError("");
    setSavedEntry(null);
    setPrintError("");
  };

  const handlePrayerChange = (index) => (e) => {
    const value = e.target.value.slice(0, PRAYER_DESCRIPTION_LIMIT);
    setPrayers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleCalculate = () => {
    setError("");
    setErrorField("");
    setResult(null);
    setAdminExpense("");
    setSubmitError("");
    setSavedEntry(null);
    setPrintError("");

    const amount = parseFloat(sumOfDispute);
    if (isNaN(amount) || amount <= 0) {
      setError("Please enter a valid positive claim amount.");
      setErrorField("sum");
      return;
    }
    if (!hasValidPrayerCount) {
      setError("Please enter the number of prayers (1 or more).");
      setErrorField("prayers");
      return;
    }
    if (prayerCount > MAX_PRAYERS) {
      setError(`Number of prayers cannot exceed ${MAX_PRAYERS}.`);
      setErrorField("prayers");
      return;
    }

    setLoading(true);
    getCalculatedFee(sumOfDispute, proBono, prayerCount)
      .then((res) => {
        setResult(res.data);
        const expense = res.data?.administrative_expense;
        setAdminExpense(
          expense === undefined || expense === null ? "" : String(expense)
        );
        if (onCalculationComplete) {
          onCalculationComplete({
            ...res.data,
            dispute_id: selectedCase?.id,
            number_of_prayers: prayerCount,
            prayers
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Fee calculation error:", err);
        setError("Failed to calculate fee. Please check connection and try again.");
        setLoading(false);
      });
  };

  const formatCurrency = (val) => {
    if (val === undefined || val === null || val === "") return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 2
    }).format(val);
  };

  const toNumber = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  };

  // The overall total mirrors the printed Fee Schedule: arbitrator's fee +
  // administrative expenses (as edited above) + sitting fee, plus the
  // pro-bono fee when the matter qualifies.
  // The backend calls this `prayers_key`; accept `prayers_fee` too so a
  // rename on that side does not silently drop the row.
  const prayersFee = result
    ? result.prayers_fee !== undefined && result.prayers_fee !== null
      ? result.prayers_fee
      : result.prayers_key
    : undefined;
  const hasPrayersFee = prayersFee !== undefined && prayersFee !== null;

  const getOverallTotal = () => {
    if (!result) return 0;
    return (
      toNumber(result.arbitrator_fee) +
      toNumber(prayersFee) +
      toNumber(adminExpense) +
      toNumber(result.sitting_fee) +
      (proBono ? toNumber(result.probono_fee) : 0)
    );
  };

  // Store the calculation against the case so it shows up in
  // Financial > Fee Calculations.
  const handleSubmit = () => {
    setSubmitError("");
    if (!selectedCase?.id || !result) return;
    if (prayers.some((p) => !p.trim())) {
      setSubmitError("Please fill in a description for every prayer before saving.");
      return;
    }

    setSubmitting(true);
    createFeeCalculation({
      dispute_id: selectedCase.id,
      claim_amount: parseFloat(sumOfDispute),
      total_amount: getOverallTotal(),
      number_of_prayers: prayerCount,
      prayers: prayers.map((description) => ({ description: description.trim() }))
    })
      .then((res) => {
        setSavedEntry(res.data);
        setSubmitting(false);
      })
      .catch((err) => {
        console.error("Fee calculation save failed:", err);
        const data = err?.response?.data;
        setSubmitError(
          data?.message || data?.error || data?.detail ||
            "Failed to save the fee calculation. Please try again."
        );
        setSubmitting(false);
      });
  };

  // Downloads the PDF report for the calculation that was just saved.
  // Only enabled once savedEntry exists (i.e. after a successful Save).
  const handlePrint = () => {
    setPrintError("");
    const feeId = savedEntry?.id;
    if (!feeId || !result) return;

    setPrinting(true);
    downloadFeeCalculationReport(feeId, {
      arbitrator_fee: result.arbitrator_fee,
      prayers_fee: hasPrayersFee ? prayersFee : null,
      administrative_expense: adminExpense === "" ? null : parseFloat(adminExpense),
      sitting_fee: result.sitting_fee,
      tentative_sittings: result.tentative_sittings || null,
      pro_bono: proBono,
      probono_fee: proBono ? result.probono_fee : null,
    })
      .then((pdfRes) => {
        const blob = new Blob([pdfRes.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `fee_calculation_${feeId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setPrinting(false);
      })
      .catch((err) => {
        console.error("Fee calculation report download failed:", err);
        setPrintError("Failed to download the report. Please try again.");
        setPrinting(false);
      });
  };

  const sittingFeeLabel = result?.tentative_sittings
    ? `Sitting Fee (tentatively for ${result.tentative_sittings} sittings)`
    : "Sitting Fee";

  const refNo = getCaseReferenceNumber(selectedCase);

  return (
    <Paper className={classes.root}>

      {/* Step 1 - find the case the fee is being valued for. */}
      {!selectedCase ? (
        <Box>
          <Typography className={classes.sectionTitle}>Search Case</Typography>
          <SearchCaseForm
            classes={{ paper: "", button: "" }}
            handleSearchSuccess={handleCaseSearchSuccess}
          />
        </Box>
      ) : (
        <>
          {/* Step 2 - basic details of the case that was found. */}
          <Box className={classes.caseCard}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={2}
            >
              <Typography
                className={classes.sectionTitle}
                style={{ marginBottom: 0 }}
              >
                Case Details
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleChangeCase}
                style={{ textTransform: "none" }}
              >
                Change
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={refNo ? 3 : 4}>
                <Typography className={classes.detailLabel}>Case No.</Typography>
                <Typography className={classes.detailValue}>
                  {selectedCase.tiac_custom_dispute_id || "N/A"}
                </Typography>
              </Grid>
              {refNo && (
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.detailLabel}>{refNo.label}</Typography>
                  <Typography className={classes.detailValue}>{refNo.value}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={refNo ? 6 : 8}>
                <Typography className={classes.detailLabel}>Case Title</Typography>
                <Typography className={classes.detailValue}>
                  {selectedCase.name || "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Step 3 - the calculator itself. */}
          <Typography className={classes.sectionTitle}>Fee Calculator</Typography>

          <Grid container spacing={3}>
            {/* Currency (Informational Box display) */}
            <Grid item xs={12}>
              <Box
                p={2}
                style={{
                  border: "1px solid #E5E5E5",
                  borderRadius: "4px",
                  backgroundColor: "#fafafa",
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                <Typography
                  style={{
                    fontWeight: 600,
                    color: TEXT.primary,
                    fontSize: "0.875rem",
                    marginRight: 8
                  }}
                >
                  Currency:
                </Typography>
                <Typography
                  style={{ fontWeight: 500, color: TEXT.secondary, fontSize: "0.875rem" }}
                >
                  {currency}
                </Typography>
              </Box>
            </Grid>

            {/* Sum in Dispute Input */}
            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>
                Sum in Dispute ({currency})
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                placeholder="Enter claim amount"
                value={sumOfDispute}
                onChange={(e) => {
                  if (isAllowedAmountInput(e.target.value)) setSumOfDispute(e.target.value);
                }}
                error={errorField === "sum"}
              />
            </Grid>

            {/* No. of Prayers */}
            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>No. of Prayers</Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                placeholder="Enter number of prayers"
                value={numberOfPrayers}
                onChange={(e) => setNumberOfPrayers(e.target.value)}
                inputProps={{ min: 1, max: MAX_PRAYERS }}
                error={
                  errorField === "prayers" ||
                  (hasValidPrayerCount && prayerCount > MAX_PRAYERS)
                }
                helperText={
                  hasValidPrayerCount && prayerCount > MAX_PRAYERS
                    ? `A maximum of ${MAX_PRAYERS} prayers can be listed.`
                    : ""
                }
              />
            </Grid>

            {/* One description input per prayer, capped at 200 characters. */}
            {prayers.length > 0 && (
              <Grid item xs={12}>
                <Typography className={classes.label}>Prayer Descriptions</Typography>
                <Grid container spacing={2}>
                  {prayers.map((prayer, index) => (
                    <Grid item xs={12} key={index}>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label={`Prayer ${index + 1}`}
                        placeholder="Describe the relief claimed"
                        value={prayer}
                        onChange={handlePrayerChange(index)}
                        inputProps={{ maxLength: PRAYER_DESCRIPTION_LIMIT }}
                        helperText={`${prayer.length}/${PRAYER_DESCRIPTION_LIMIT} characters`}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {/* Pro-Bono Checkbox (Conditionally visible) */}
            {isProBonoAvailable && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={proBono}
                      onChange={(e) => setProBono(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Pro-Bono"
                />
              </Grid>
            )}
          </Grid>

          {error && <Typography className={classes.errorText}>{error}</Typography>}

          {/* Calculate Button */}
          <Box display="flex" justifyContent="flex-start" mt={2}>
            <Button
              className={classes.calculateBtn}
              variant="contained"
              color="primary"
              onClick={handleCalculate}
              disabled={loading || !sumOfDispute || !numberOfPrayers}
            >
              {loading ? (
                <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                  <CircularProgress size={16} color="inherit" />
                  <span>Calculating...</span>
                </Box>
              ) : (
                "Calculate"
              )}
            </Button>
          </Box>

          {/* Result Section */}
          {result && (
            <Box className={classes.resultCard}>
              <Typography className={classes.resultTitle}>Fee Details</Typography>

              {/* Total Fee Section */}
              <Box className={classes.totalFeeCard}>
                <Typography className={classes.totalFeeLabel}>
                  Total Estimated Fee
                </Typography>
                <Typography className={classes.totalFeeValue}>
                  {formatCurrency(getOverallTotal())}
                </Typography>
              </Box>

              <Divider style={{ marginBottom: 16 }} />

              {/* Administrative Expense - editable, feeds the total above */}
              <Box mb={3}>
                <Box className={classes.feeRow}>
                  <Typography className={classes.feeLabel}>
                    Administrative Expenses
                  </Typography>
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    className={classes.editableFeeInput}
                    value={adminExpense}
                    onChange={(e) => {
                      if (isAllowedAmountInput(e.target.value)) setAdminExpense(e.target.value);
                    }}
                    inputProps={{
                      min: 0,
                      style: { textAlign: "right", fontWeight: 600 }
                    }}
                  />
                </Box>
                <Box className={classes.noteBox}>
                  <Typography className={classes.noteText}>
                    The Administrative Expenses above may be revised; the Total
                    Estimated Fee updates accordingly. The above expenses shall be
                    shared equally by all the parties.
                  </Typography>
                </Box>
              </Box>

              {/* Arbitrator's Fee */}
              <Box mb={3}>
                <Box className={classes.feeRow}>
                  <Typography className={classes.feeLabel}>
                    Arbitrator's Fee
                  </Typography>
                  <Typography className={classes.feeValue}>
                    {formatCurrency(result.arbitrator_fee)}
                  </Typography>
                </Box>

                {/* Prayers Fee rides along with the Arbitrator's Fee. */}
                {hasPrayersFee && (
                  <Box className={classes.feeRow}>
                    <Box>
                      <Typography className={classes.feeLabel}>
                        <span className={classes.plusSign}>+</span>
                        Prayers Fee
                      </Typography>
                      <Typography className={classes.feeSubLabel}>
                        {formatCurrency(PRAYER_FEE_PER_PRAYER)} per prayer
                      </Typography>
                    </Box>
                    <Typography className={classes.feeValue}>
                      {formatCurrency(prayersFee)}
                    </Typography>
                  </Box>
                )}

                <Box className={classes.noteBox} style={{ marginBottom: 8 }}>
                  <Typography className={classes.noteText}>
                    The fee fixed above is in respect of a single Arbitrator. The fee at the same rate shall be payable to any additional Arbitrator.
                  </Typography>
                </Box>
                <Box className={classes.noteBox}>
                  <Typography className={classes.noteText}>
                    In the event of an Arbitrator chosen from the Panel of Arbitrators, or nominated who is not on the panel and is from a place other than Bengaluru, the party nominating such Arbitrator shall alone bear all expenses of such Arbitrator, apart from the fee payable as per the schedule, for participation in the arbitration proceedings.
                  </Typography>
                </Box>
              </Box>

              {/* Sitting Fee */}
              <Box mb={3}>
                <Box className={classes.feeRow}>
                  <Typography className={classes.feeLabel}>
                    {sittingFeeLabel}
                  </Typography>
                  <Typography className={classes.feeValue}>
                    {formatCurrency(result.sitting_fee)}
                  </Typography>
                </Box>
                <Box className={classes.noteBox}>
                  <Typography className={classes.noteText}>
                    In addition to the Administrative Expenses as above, the parties shall also pay a sum of ₹5,000/- (Rupees five thousand only) per day (irrespective of the duration of the sitting on a given day) for the use of the facilities of the Centre on the days the Arbitral Tribunal holds its sittings. The above expenses shall be shared equally by all the parties.
                  </Typography>
                </Box>
              </Box>

              {/* Pro-Bono Fee (Conditional display) */}
              {proBono &&
                result.probono_fee !== null &&
                result.probono_fee !== undefined && (
                  <Box mb={2}>
                    <Box className={classes.feeRow}>
                      <Typography className={classes.feeLabel}>
                        Pro-Bono Fee
                      </Typography>
                      <Typography className={classes.feeValue}>
                        {formatCurrency(result.probono_fee)}
                      </Typography>
                    </Box>
                  </Box>
                )}

              {/* Save the calculation to the Fee Calculations register. */}
              <Box display="flex" alignItems="stretch" mt={3} style={{ gap: "1rem" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={submitting || Boolean(savedEntry)}
                  style={{ textTransform: "none", fontWeight: 600 }}
                >
                  {submitting ? (
                    <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                      <CircularProgress size={16} color="inherit" />
                      <span>Saving...</span>
                    </Box>
                  ) : savedEntry ? (
                    "Saved"
                  ) : (
                    "Save"
                  )}
                </Button>

                {savedEntry && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePrint}
                    disabled={printing}
                    style={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {printing ? (
                      <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                        <CircularProgress size={16} color="inherit" />
                        <span>Preparing...</span>
                      </Box>
                    ) : (
                      "Print"
                    )}
                  </Button>
                )}

                {savedEntry && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleChangeCase}
                    style={{ textTransform: "none" }}
                  >
                    New Calculation
                  </Button>
                )}
              </Box>

              {submitError && (
                <Typography className={classes.errorText}>{submitError}</Typography>
              )}

              {printError && (
                <Typography className={classes.errorText}>{printError}</Typography>
              )}

              {savedEntry && (
                <Box className={classes.successBox}>
                  Fee calculation saved. It is now listed under Financial &gt; Fee
                  Calculations.
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}